import React, { useState, useEffect } from 'react';
import { Users, Send, CheckCircle } from 'lucide-react';
import orgApi from '../api/organizations';
import ConfirmModal from '../components/UI/ConfirmModal';

const ROLE_STYLES = {
  Admin: 'bg-amber-400/10 text-amber-400 border border-amber-400/20',
};

const daysUntil = (isoDate) => {
  const diff = new Date(isoDate) - new Date();
  return Math.max(0, Math.floor(diff / 86400000));
};

const timeAgo = (isoDate) => {
  if (!isoDate) return '—';
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const OrgMembersMembers = ({ tenantId }) => {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteWarn, setInviteWarn] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [editRoleValue, setEditRoleValue] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null, target: null });
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [membersData, rolesData] = await Promise.all([
        orgApi.getMembers(tenantId),
        orgApi.getRoles(tenantId).catch(() => []),
      ]);
      setMembers(membersData?.members || membersData?.users || []);
      setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.roles || []);
      if (rolesData && rolesData.length > 0 && !inviteRole) {
        setInviteRole(rolesData[0].name);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError(null);
    setInviteWarn(null);
    setInviteSuccess(null);
    setInviteLink(null);
    setCopied(false);
    if (!inviteEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setInviteError('Enter a valid email address.');
      return;
    }
    setInviting(true);
    try {
      const res = await orgApi.createInvitation(tenantId, inviteEmail, inviteRole);
      setInviteLink(res.invite_link || null);
      if (res.email_sent) {
        setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      } else {
        setInviteWarn('Invitation saved but email delivery failed — share the invite link manually.');
      }
      setInviteEmail('');
      load();
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to send invitation.');
    } finally {
      setInviting(false);
    }
  };

  const handleSaveRole = async (memberId) => {
    try {
      await orgApi.updateMemberRole(tenantId, memberId, editRoleValue);
      setEditingRole(null);
      load();
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to update role.');
    }
  };

  const handleConfirmAction = async () => {
    const { type, target } = confirmModal;
    setConfirmModal({ open: false });
    setConfirming(true);
    try {
      if (type === 'remove') {
        await orgApi.removeMember(tenantId, target.id);
      } else if (type === 'revoke') {
        await orgApi.revokeInvitation(tenantId, target.id);
      }
      load();
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Action failed.');
    } finally {
      setConfirming(false);
    }
  };

  const handleResend = async (inv) => {
    try {
      const res = await orgApi.resendInvitation(tenantId, inv.id);
      setInviteLink(res.invite_link || null);
      setCopied(false);
      if (res.email_sent) {
        setInviteSuccess(`Invitation resent to ${inv.email}`);
      } else {
        setInviteWarn('New invite link generated — share it with the user.');
      }
    } catch {
      setInviteError('Failed to resend invitation.');
    }
  };

  const filtered = members.filter(m =>
    m.email.toLowerCase().includes(search.toLowerCase()) ||
    (m.full_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const active = members.filter(m => m.status === 'active').length;
  const pending = members.filter(m => m.status === 'pending').length;

  const getRoleStyle = (roleName) => {
    if (ROLE_STYLES[roleName]) return ROLE_STYLES[roleName];
    return 'bg-gray-700/50 text-gray-400 border border-gray-600/50';
  };

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Active Members', value: active, color: 'text-emerald-400' },
          { label: 'Pending Invites', value: pending, color: 'text-amber-400' },
          { label: 'Total Seats', value: active + pending, color: 'text-blue-400' },
          { label: 'Roles In Use', value: new Set(members.map(m => m.role)).size, color: 'text-gray-300' },
        ].map(s => (
          <div key={s.label} className="bg-gray-900 border border-gray-700/50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Invite form */}
      <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Send className="w-3.5 h-3.5 text-cyan-400" /> Invite New Member
        </p>
        <form onSubmit={handleInvite} className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1.5">Email Address</label>
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
            />
          </div>
          <div className="w-48">
            <label className="block text-xs text-gray-500 mb-1.5">Role</label>
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
            >
              {roles.map(r => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={inviting}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5" />
            {inviting ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
        {inviteError && <p className="mt-2 text-xs text-red-400">{inviteError}</p>}
        {inviteSuccess && <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle className="w-3.5 h-3.5" />{inviteSuccess}</p>}
        {inviteWarn && <p className="mt-2 text-xs text-amber-400">{inviteWarn}</p>}
        {inviteLink && (
          <div className="mt-2 flex items-center gap-2 bg-gray-950 border border-gray-700 rounded-lg px-3 py-2">
            <code className="flex-1 text-xs text-cyan-400 break-all select-all">{inviteLink}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(inviteLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="shrink-0 text-xs px-2 py-1 border border-gray-600 rounded text-gray-400 hover:text-white hover:border-gray-400 transition-colors"
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      {/* Members table */}
      <div className="bg-gray-900 border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/50">
          <span className="text-sm font-semibold text-gray-300 flex items-center gap-2">
            <Users className="w-4 h-4 text-cyan-400" /> Members &amp; Invitations
          </span>
          <input
            placeholder="Search email or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-xs text-gray-300 focus:outline-none focus:border-cyan-500 w-48 transition-colors"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">No members found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700/50">
                {['Member', 'Role', 'Status', 'Last Login', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id} className={`border-b border-gray-700/30 hover:bg-gray-800/30 transition-colors ${m.status === 'pending' ? 'opacity-75' : ''}`}>
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-medium text-white">{m.email}</p>
                    {m.full_name && <p className="text-xs text-gray-500">{m.full_name}</p>}
                    {m.status === 'pending' && (
                      <p className="text-xs text-amber-500 mt-0.5">Invite sent · expires in {daysUntil(m.expires_at)}d</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {editingRole === m.id ? (
                      <div className="flex gap-2 items-center">
                        <select
                          value={editRoleValue}
                          onChange={e => setEditRoleValue(e.target.value)}
                          className="px-2 py-1 bg-gray-950 border border-gray-600 rounded text-xs text-white"
                        >
                          {roles.map(r => (
                            <option key={r.id} value={r.name}>{r.name}</option>
                          ))}
                        </select>
                        <button onClick={() => handleSaveRole(m.id)} className="text-xs text-emerald-400 hover:text-emerald-300">Save</button>
                        <button onClick={() => setEditingRole(null)} className="text-xs text-gray-500">✕</button>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleStyle(m.role)}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {m.role}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      m.status === 'active'
                        ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20'
                        : 'bg-amber-400/10 text-amber-400 border border-amber-400/20'
                    }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {m.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-gray-500">{timeAgo(m.last_login)}</td>
                  <td className="px-5 py-3.5">
                    {m.type === 'member' ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingRole(m.id); setEditRoleValue(m.role); }}
                          className="px-2.5 py-1 text-xs text-blue-400 bg-blue-400/10 border border-blue-400/20 rounded-md hover:bg-blue-400/20 transition-colors"
                        >
                          Change Role
                        </button>
                        <button
                          onClick={() => setConfirmModal({ open: true, type: 'remove', target: m })}
                          className="px-2.5 py-1 text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-md hover:bg-red-400/20 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleResend(m)}
                          className="px-2.5 py-1 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-md hover:bg-amber-400/20 transition-colors"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => setConfirmModal({ open: true, type: 'revoke', target: m })}
                          className="px-2.5 py-1 text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-md hover:bg-red-400/20 transition-colors"
                        >
                          Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.type === 'remove' ? 'Remove Member' : 'Revoke Invitation'}
        message={confirmModal.type === 'remove'
          ? `Remove ${confirmModal.target?.email} from this organization? Their active session will be invalidated within 60 seconds.`
          : `Revoke the invitation for ${confirmModal.target?.email}?`}
        confirmLabel={confirmModal.type === 'remove' ? 'Remove' : 'Revoke'}
        danger
        disabled={confirming}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmModal({ open: false })}
      />
    </div>
  );
};

export default OrgMembersMembers;
